import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  InputAdornment,
  Paper,
  Slider,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/SearchOutlined';
import InventoryIcon from '@mui/icons-material/Inventory2';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import CategoryIcon from '@mui/icons-material/Category';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router';

import {
  AdminTaskRow,
  adminTaskApi,
  CreateTaskPayload,
} from '../../../../api/adminTaskApi';
import { adminCatalogApi, CatalogRow } from '../../../../api/adminCatalogApi';
import { useCurrentUser } from '../../../../auth/useCurrentUser';
import { paths } from '../../../../routes/paths';
import {
  colors,
  primaryButtonSx,
  secondaryButtonSx,
  statusChipColors,
  tableCellSx,
  tableHeadCellSx,
  typo,
} from '../../../../theme/tokens';

import PublishConfirmationDialog, {
  PublishEntity,
  toPublishEntity,
} from './PublishConfirmationDialog';
import DependencyImpactDialog from './DependencyImpactDialog';
import { WriteButton } from '../../../../components/ui';

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

/**
 * Status chip colors per spec: Unpublished (amber), In sync (green),
 * Never published (grey). Kept inline instead of using the shared
 * `statusChipColors` token so we can map three admin-specific statuses
 * without muddying the tenant-side chip palette.
 */
function statusChipProps(status: AdminTaskRow['status']): {
  label: string;
  bg: string;
  color: string;
} {
  switch (status) {
    case 'unpublished':
      return { label: 'Unpublished', bg: colors.warningBgStrong, color: colors.warningText };
    case 'in_sync':
      return { label: 'In sync', bg: colors.successBgStrong, color: colors.successText };
    case 'never_published':
    default:
      return { label: 'Never published', bg: colors.bgMuted, color: colors.textSecondary };
  }
}

/**
 * Default sort comparator: Unpublished → In sync → Never published, then
 * `updated_at` DESC. Kept inline because React Query's `select` couldn't
 * express it cleanly as a sort param on the backend (backend supports
 * single-column sort; composite ordering is a UI concern).
 */
const STATUS_ORDER: Record<AdminTaskRow['status'], number> = {
  unpublished: 0,
  in_sync: 1,
  never_published: 2,
};

function defaultSort(rows: AdminTaskRow[]): AdminTaskRow[] {
  return [...rows].sort((a, b) => {
    const sa = STATUS_ORDER[a.status] ?? 99;
    const sb = STATUS_ORDER[b.status] ?? 99;
    if (sa !== sb) return sa - sb;
    return (b.updated_at || '').localeCompare(a.updated_at || '');
  });
}

function formatRelative(iso: string | null | undefined): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const diffMs = Date.now() - then;
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return 'just now';
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mo = Math.round(day / 30);
  if (mo < 12) return `${mo}mo ago`;
  const yr = Math.round(mo / 12);
  return `${yr}y ago`;
}

function initialsFor(email: string | null | undefined): string {
  if (!email) return '?';
  const local = email.split('@')[0] || email;
  const parts = local.split(/[._-]/).filter(Boolean);
  const picks = parts.length >= 2 ? [parts[0][0], parts[1][0]] : [local[0]];
  return picks.join('').toUpperCase().slice(0, 2);
}

/* ------------------------------------------------------------------ */
/*  Time-chip windows (Today / 7d / 30d / All)                         */
/* ------------------------------------------------------------------ */

type TimeChip = 'today' | '7d' | '30d' | 'all';
const TIME_CHIPS: { key: TimeChip; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: '7d', label: '7d' },
  { key: '30d', label: '30d' },
  { key: 'all', label: 'All' },
];

function matchesTimeChip(iso: string | null | undefined, chip: TimeChip): boolean {
  if (chip === 'all' || !iso) return chip === 'all' ? true : false;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return false;
  const diffDays = (Date.now() - then) / (1000 * 60 * 60 * 24);
  if (chip === 'today') return diffDays < 1;
  if (chip === '7d') return diffDays < 7;
  if (chip === '30d') return diffDays < 30;
  return true;
}

/* ------------------------------------------------------------------ */
/*  Empty state                                                        */
/* ------------------------------------------------------------------ */

const EmptyState: React.FC<{
  onCreate: () => void;
  onOpenCatalogs: () => void;
}> = ({ onCreate, onOpenCatalogs }) => (
  <Paper
    elevation={0}
    sx={{
      borderRadius: '12px',
      border: `1px solid ${colors.borderLight}`,
      backgroundColor: colors.bgWhite,
      py: 8,
      px: 4,
      textAlign: 'center',
    }}
  >
    <Box
      sx={{
        width: 96,
        height: 96,
        borderRadius: '50%',
        backgroundColor: colors.adminAccentSubtle,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        mb: 3,
      }}
    >
      <InventoryIcon sx={{ fontSize: 64, color: colors.adminAccent }} />
    </Box>
    <Typography
      variant="h2"
      sx={{ fontSize: 24, fontWeight: 800, fontFamily: 'Manrope, sans-serif', mb: 1 }}
    >
      No tasks yet
    </Typography>
    <Typography sx={{ ...typo.secondaryText, maxWidth: 520, mx: 'auto', mb: 3 }}>
      Platform tasks are the reusable action templates that tenants pick from. Create one to get
      started.
    </Typography>
    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 3 }}>
      <WriteButton variant="contained" onClick={onCreate} sx={primaryButtonSx} startIcon={<AddIcon />}>
        Create first task
      </WriteButton>
      <Button onClick={onOpenCatalogs} sx={{ ...secondaryButtonSx, fontWeight: 600 }}>
        Open Reference Catalogs →
      </Button>
    </Box>
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'flex-start',
        gap: 1.5,
        px: 2,
        py: 1.5,
        mx: 'auto',
        maxWidth: 560,
        borderRadius: '8px',
        backgroundColor: colors.infoBg,
        border: `1px solid ${colors.infoBorder}`,
        textAlign: 'left',
      }}
    >
      <CategoryIcon sx={{ fontSize: 18, color: colors.infoIcon, mt: 0.25, flexShrink: 0 }} />
      <Typography sx={{ fontSize: 13, color: colors.infoText, lineHeight: 1.5 }}>
        <strong>Tip:</strong> <code>task_type</code> and <code>task_category</code> are required
        FKs. If they&apos;re empty, the Create dialog will prompt you to open the catalog first.
      </Typography>
    </Box>
  </Paper>
);

/* ------------------------------------------------------------------ */
/*  Create dialog                                                      */
/* ------------------------------------------------------------------ */

interface CreateDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (row: AdminTaskRow, opts: { published: boolean }) => void;
}

const CreateTaskDialog: React.FC<CreateDialogProps> = ({ open, onClose, onCreated }) => {
  const navigate = useNavigate();
  const { isSuperAdmin } = useCurrentUser();
  const { data: types = [] } = useQuery<CatalogRow[]>({
    queryKey: ['admin', 'catalogs', 'task-types'],
    queryFn: () => adminCatalogApi.list('task-types'),
    enabled: open,
  });
  const { data: cats = [] } = useQuery<CatalogRow[]>({
    queryKey: ['admin', 'catalogs', 'task-categories'],
    queryFn: () => adminCatalogApi.list('task-categories'),
    enabled: open,
  });

  const [form, setForm] = useState<CreateTaskPayload>({
    task_name: '',
    task_type_code: '',
    task_category_code: '',
    self_report: false,
    confirm_report: false,
    is_subtask: false,
  });
  const [error, setError] = useState<string | null>(null);
  // Tracks which button is in-flight so we can show distinct labels and only
  // disable the action that's actually running. `null` when idle.
  const [pendingAction, setPendingAction] = useState<'save' | 'save-publish' | null>(null);

  useEffect(() => {
    if (open) {
      setForm({
        task_name: '',
        task_type_code: '',
        task_category_code: '',
        self_report: false,
        confirm_report: false,
        is_subtask: false,
      });
      setError(null);
      setPendingAction(null);
    }
  }, [open]);

  const errorMessage = (err: any, fallback: string): string => {
    const code = err?.response?.data?.error;
    if (code === 'conflict') return 'A task with this code already exists.';
    if (code === 'platform_lookup_failed') {
      return 'Publish failed: task type or category not seeded on the platform yet.';
    }
    if (code === 'platform_fk_missing') {
      return 'Publish failed: platform foreign key missing — seed task_type / task_category first.';
    }
    if (code === 'platform_write_blocked') {
      return 'Publish failed: server is configured against the wrong database.';
    }
    if (typeof code === 'string') return `${fallback}: ${code}`;
    return `${fallback}. Try again.`;
  };

  const handleSave = async (publishAfter: boolean) => {
    setError(null);
    setPendingAction(publishAfter ? 'save-publish' : 'save');
    try {
      const row = await adminTaskApi.create(form);
      if (publishAfter) {
        try {
          await adminTaskApi.publishSingle(row.task_code, row.current_version);
        } catch (pubErr) {
          // Save succeeded but publish failed — surface the error and let
          // the parent know the row exists (so the list refreshes) without
          // marking it as published.
          setError(errorMessage(pubErr, 'Saved, but publish failed'));
          onCreated(row, { published: false });
          setPendingAction(null);
          return;
        }
      }
      onCreated(row, { published: publishAfter });
      onClose();
    } catch (err) {
      setError(errorMessage(err, 'Save failed'));
    } finally {
      setPendingAction(null);
    }
  };

  const typesEmpty = types.length === 0;
  const catsEmpty = cats.length === 0;

  const canSubmit =
    !!form.task_name.trim() && !!form.task_type_code && !!form.task_category_code;
  const isPending = pendingAction !== null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}>
        Create task
      </DialogTitle>
      <DialogContent dividers>
        {(typesEmpty || catsEmpty) && (
          <Alert
            severity="warning"
            sx={{ mb: 2, borderRadius: '8px' }}
            action={
              <Button
                size="small"
                onClick={() => {
                  onClose();
                  navigate(paths.admin.catalogs.root());
                }}
              >
                Open catalogs
              </Button>
            }
          >
            {typesEmpty && catsEmpty
              ? 'Task types and task categories are empty — create at least one of each first.'
              : typesEmpty
                ? 'No task types exist yet — create at least one first.'
                : 'No task categories exist yet — create at least one first.'}
          </Alert>
        )}

        <Box sx={{ display: 'grid', gap: 2, mt: 1 }}>
          <TextField
            size="small"
            label="Task name"
            value={form.task_name}
            onChange={(e) => setForm({ ...form, task_name: e.target.value })}
            inputProps={{ maxLength: 80 }}
            helperText={`${form.task_name.length} / 80 — task_code is generated automatically`}
          />
          <TextField
            size="small"
            select
            SelectProps={{ native: true }}
            label="Task type"
            value={form.task_type_code}
            onChange={(e) => setForm({ ...form, task_type_code: e.target.value })}
            disabled={typesEmpty}
          >
            <option value="" disabled>
              — Select —
            </option>
            {types.map((t) => (
              <option key={t.code} value={t.code}>
                {t.name} ({t.code})
              </option>
            ))}
          </TextField>
          <TextField
            size="small"
            select
            SelectProps={{ native: true }}
            label="Task category"
            value={form.task_category_code}
            onChange={(e) => setForm({ ...form, task_category_code: e.target.value })}
            disabled={catsEmpty}
          >
            <option value="" disabled>
              — Select —
            </option>
            {cats.map((c) => (
              <option key={c.code} value={c.code}>
                {c.name} ({c.code})
              </option>
            ))}
          </TextField>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={!!form.self_report}
                  onChange={(e) => setForm({ ...form, self_report: e.target.checked })}
                />
              }
              label={<Typography sx={{ fontSize: 13 }}>Self-report</Typography>}
            />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={!!form.confirm_report}
                  onChange={(e) => setForm({ ...form, confirm_report: e.target.checked })}
                />
              }
              label={<Typography sx={{ fontSize: 13 }}>Requires confirmation</Typography>}
            />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={!!form.is_subtask}
                  onChange={(e) => setForm({ ...form, is_subtask: e.target.checked })}
                />
              }
              label={<Typography sx={{ fontSize: 13 }}>Is subtask</Typography>}
            />
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2, borderRadius: '8px' }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={secondaryButtonSx} disabled={isPending}>
          Cancel
        </Button>
        <WriteButton
          variant="outlined"
          disabled={!canSubmit || isPending}
          onClick={() => handleSave(false)}
          sx={secondaryButtonSx}
        >
          {pendingAction === 'save' ? 'Saving…' : 'Save'}
        </WriteButton>
        <Tooltip
          title={
            isSuperAdmin
              ? 'Save and publish this task to platform task.task'
              : 'Publish requires super-admin role'
          }
        >
          <span>
            <WriteButton
              variant="contained"
              disabled={!canSubmit || isPending || !isSuperAdmin}
              onClick={() => handleSave(true)}
              sx={primaryButtonSx}
            >
              {pendingAction === 'save-publish' ? 'Publishing…' : 'Save & Publish'}
            </WriteButton>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

/* ------------------------------------------------------------------ */
/*  Main list page                                                     */
/* ------------------------------------------------------------------ */

const STATUS_FILTER_OPTIONS: AdminTaskRow['status'][] = [
  'unpublished',
  'in_sync',
  'never_published',
];

const TasksList: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isSuperAdmin } = useCurrentUser();

  const searchRef = useRef<HTMLInputElement | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<AdminTaskRow['status'][]>([]);
  const [usedByMin, setUsedByMin] = useState<number>(0);
  const [timeChip, setTimeChip] = useState<TimeChip>('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [createOpen, setCreateOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [deactivateFor, setDeactivateFor] = useState<AdminTaskRow | null>(null);
  // `includeInactive` is URL-synced (?includeInactive=1) so copy-pasting
  // a filter-view link yields the same rows. Default OFF — inactive rows
  // are intentionally opt-in noise.
  const includeInactive = searchParams.get('includeInactive') === '1';
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    msg: string;
    sev: 'success' | 'error' | 'info';
  }>({ open: false, msg: '', sev: 'success' });

  const pageParam = Number(searchParams.get('page') || '1');
  const pageSizeParam = Number(searchParams.get('pageSize') || '50');
  const page = Number.isFinite(pageParam) && pageParam > 0 ? pageParam - 1 : 0;
  const pageSize = [25, 50, 100, 200].includes(pageSizeParam) ? pageSizeParam : 50;

  const { data: rows = [], isLoading } = useQuery<AdminTaskRow[]>({
    queryKey: ['admin', 'tasks', 'list'],
    queryFn: () => adminTaskApi.list(),
  });

  // ── Keyboard: "/" focuses search ──
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key !== '/') return;
      const tag = (ev.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      ev.preventDefault();
      searchRef.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // ── Esc clears selection ──
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape' && selected.size > 0) setSelected(new Set());
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected.size]);

  // ── "P" opens publish when selection is non-empty (super-admin only) ──
  // Gated by role + selection to prevent accidental opens while typing
  // in the list view outside an input. Shortcut disabled if publish is
  // already open (prevents stacking). Target guard also checks
  // `isContentEditable` — ContextField / richer editors still rely on
  // native typing there and we've been bitten by shortcut hijack before.
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      const target = ev.target as HTMLElement | null;
      const tag = target?.tagName;
      const isEditable = target?.isContentEditable;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || isEditable) return;
      if (ev.metaKey || ev.ctrlKey || ev.altKey) return;
      if (ev.key !== 'p' && ev.key !== 'P') return;
      if (!isSuperAdmin || selected.size === 0 || publishOpen) return;
      ev.preventDefault();
      setPublishOpen(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isSuperAdmin, selected.size, publishOpen]);

  const sorted = useMemo(() => defaultSort(rows), [rows]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return sorted.filter((r) => {
      // Inactive rows are hidden by default; include only when the
      // filter-bar switch is on (and persisted in the URL).
      if (!includeInactive && !r.active) return false;
      if (needle) {
        const hay = `${r.task_code} ${r.task_name ?? ''}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      if (statusFilter.length && !statusFilter.includes(r.status)) return false;
      if (!matchesTimeChip(r.updated_at, timeChip)) return false;
      // Round-2 note: "Used by N" isn't wired on the backend yet — treat
      // missing usage count as 0 so the slider is still interactive (user
      // can at least filter "0-usage" entities). Real counts land in R3.
      const usedBy = 0;
      if (usedBy < usedByMin) return false;
      return true;
    });
  }, [sorted, search, statusFilter, timeChip, usedByMin, includeInactive]);

  const paged = useMemo(() => {
    const start = page * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const updateSearchParam = useCallback(
    (next: { page?: number; pageSize?: number }) => {
      const nsp = new URLSearchParams(searchParams);
      if (next.page !== undefined) nsp.set('page', String(next.page + 1));
      if (next.pageSize !== undefined) nsp.set('pageSize', String(next.pageSize));
      setSearchParams(nsp, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const toggleIncludeInactive = useCallback(
    (next: boolean) => {
      const nsp = new URLSearchParams(searchParams);
      if (next) nsp.set('includeInactive', '1');
      else nsp.delete('includeInactive');
      setSearchParams(nsp, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const toggleOne = (code: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const allPagedSelected = paged.length > 0 && paged.every((r) => selected.has(r.task_code));
  const somePagedSelected = paged.some((r) => selected.has(r.task_code));

  const toggleAllPaged = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allPagedSelected) paged.forEach((r) => next.delete(r.task_code));
      else paged.forEach((r) => next.add(r.task_code));
      return next;
    });
  };

  const handleCreated = (row: AdminTaskRow, opts: { published: boolean }) => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'tasks', 'list'] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'tasks', row.task_code] });
    setSnackbar({
      open: true,
      msg: opts.published
        ? `Created and published ${row.task_code}`
        : `Created ${row.task_code}`,
      sev: 'success',
    });
    navigate(paths.admin.globalCatalog.tasks(row.task_code));
  };

  /* ------------------------------ */
  /*  Render                         */
  /* ------------------------------ */

  const showEmpty = !isLoading && rows.length === 0;

  return (
    <Box sx={{ maxWidth: 1400 }}>
      {/* Page header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 700,
              color: colors.textTertiary,
              textTransform: 'uppercase',
              letterSpacing: '0.6px',
              mb: 0.5,
            }}
          >
            Admin · Global Catalog
          </Typography>
          <Typography sx={{ ...typo.pageTitle, mb: 0.5 }}>Tasks</Typography>
          <Typography sx={{ ...typo.pageSubtitle }}>
            Platform-wide task templates. Tenants pick from these to build their action library.
          </Typography>
        </Box>
        <WriteButton
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
          sx={primaryButtonSx}
        >
          Create task
        </WriteButton>
      </Box>

      {showEmpty ? (
        <EmptyState
          onCreate={() => setCreateOpen(true)}
          onOpenCatalogs={() => navigate(paths.admin.catalogs.root())}
        />
      ) : (
        <>
          {/* Filter bar — sticky under page header */}
          <Paper
            elevation={0}
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 5,
              mb: 2,
              p: 2,
              borderRadius: '12px',
              border: `1px solid ${colors.borderLight}`,
              backgroundColor: colors.bgWhite,
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <TextField
              size="small"
              placeholder="Search code or name… (press /)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              inputRef={searchRef}
              sx={{ minWidth: 260, flex: '1 1 260px' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: colors.textTertiary }} />
                  </InputAdornment>
                ),
              }}
            />

            <ToggleButtonGroup
              size="small"
              value={statusFilter}
              onChange={(_, v) => setStatusFilter(v as AdminTaskRow['status'][])}
              aria-label="Status filter"
            >
              {STATUS_FILTER_OPTIONS.map((s) => {
                const cp = statusChipProps(s);
                return (
                  <ToggleButton
                    key={s}
                    value={s}
                    sx={{
                      textTransform: 'none',
                      fontSize: 12,
                      fontWeight: 600,
                      px: 1.5,
                      '&.Mui-selected': { backgroundColor: cp.bg, color: cp.color },
                    }}
                  >
                    {cp.label}
                  </ToggleButton>
                );
              })}
            </ToggleButtonGroup>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: 200 }}>
              <Typography sx={{ fontSize: 12, color: colors.textSecondary, whiteSpace: 'nowrap' }}>
                Used by &gt; {usedByMin}
              </Typography>
              <Slider
                size="small"
                min={0}
                max={50}
                value={usedByMin}
                onChange={(_, v) => setUsedByMin(v as number)}
                sx={{ color: colors.adminAccent }}
              />
            </Box>

            {/*
              Show-inactive switch — placed after status filter per UX brief,
              before the divider/time chips. URL-synced so a link carries the
              view state.
            */}
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={includeInactive}
                  onChange={(e) => toggleIncludeInactive(e.target.checked)}
                />
              }
              label={
                <Typography sx={{ fontSize: 12, color: colors.textSecondary }}>
                  Show inactive
                </Typography>
              }
              sx={{ m: 0 }}
            />

            <Divider orientation="vertical" flexItem sx={{ borderColor: colors.borderLight }} />

            <ToggleButtonGroup
              size="small"
              exclusive
              value={timeChip}
              onChange={(_, v) => v && setTimeChip(v as TimeChip)}
            >
              {TIME_CHIPS.map((t) => (
                <ToggleButton
                  key={t.key}
                  value={t.key}
                  sx={{
                    textTransform: 'none',
                    fontSize: 12,
                    fontWeight: 600,
                    px: 1.5,
                  }}
                >
                  {t.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Paper>

          {/* Table */}
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress sx={{ color: colors.adminAccent }} />
            </Box>
          ) : (
            <Paper
              elevation={0}
              sx={{
                borderRadius: '12px',
                border: `1px solid ${colors.borderLight}`,
                overflow: 'hidden',
              }}
            >
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...tableHeadCellSx, width: 44, px: 1 }}>
                        <Checkbox
                          size="small"
                          checked={allPagedSelected}
                          indeterminate={somePagedSelected && !allPagedSelected}
                          onChange={toggleAllPaged}
                          sx={{ p: 0 }}
                        />
                      </TableCell>
                      <TableCell sx={{ ...tableHeadCellSx, width: 220 }}>Code</TableCell>
                      <TableCell sx={tableHeadCellSx}>Name</TableCell>
                      <TableCell sx={{ ...tableHeadCellSx, width: 120 }}>Type</TableCell>
                      <TableCell sx={{ ...tableHeadCellSx, width: 140 }}>Status</TableCell>
                      <TableCell sx={{ ...tableHeadCellSx, width: 100 }}>Used by</TableCell>
                      <TableCell sx={{ ...tableHeadCellSx, width: 140 }}>Updated</TableCell>
                      <TableCell sx={{ ...tableHeadCellSx, width: 56, textAlign: 'center' }}>
                        By
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paged.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} sx={{ py: 8, textAlign: 'center' }}>
                          <Typography sx={{ ...typo.secondaryText }}>
                            No tasks match the current filters.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      paged.map((r) => {
                        const cp = statusChipProps(r.status);
                        const isSel = selected.has(r.task_code);
                        const isInactive = !r.active;
                        return (
                          <TableRow
                            key={r.task_code}
                            hover
                            sx={{
                              cursor: 'pointer',
                              // Dim inactive rows; hover restores full
                              // opacity so content stays inspectable.
                              opacity: isInactive ? 0.6 : 1,
                              transition: 'opacity 0.15s ease',
                              backgroundColor: isSel ? colors.adminAccentSubtle : undefined,
                              '&:hover': {
                                opacity: 1,
                                backgroundColor: isSel
                                  ? colors.adminAccentSubtle
                                  : colors.bgSubtle,
                              },
                            }}
                            onClick={() => navigate(paths.admin.globalCatalog.tasks(r.task_code))}
                          >
                            <TableCell
                              sx={{ ...tableCellSx, px: 1 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleOne(r.task_code);
                              }}
                            >
                              <Checkbox size="small" checked={isSel} sx={{ p: 0 }} />
                            </TableCell>
                            <TableCell sx={{ ...tableCellSx, ...typo.mono, color: colors.textPrimary }}>
                              {r.task_code}
                            </TableCell>
                            <TableCell sx={tableCellSx}>
                              {r.task_name ?? '—'}
                              {isInactive && (
                                <Typography
                                  sx={{
                                    fontSize: 11,
                                    color: colors.textTertiary,
                                    mt: 0.25,
                                  }}
                                >
                                  Deactivated on {formatRelative(r.updated_at)}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell sx={tableCellSx}>
                              {r.task_type_code ? (
                                <Chip
                                  size="small"
                                  label={r.task_type_code}
                                  sx={{
                                    fontSize: 11,
                                    fontWeight: 600,
                                    fontFamily: 'Fira Code, monospace',
                                    backgroundColor: colors.adminAccentSubtle,
                                    color: colors.adminAccent,
                                  }}
                                />
                              ) : (
                                <Typography sx={{ fontSize: 12, color: colors.textTertiary }}>
                                  —
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell sx={tableCellSx}>
                              <Chip
                                size="small"
                                label={isInactive ? 'Inactive' : cp.label}
                                sx={{
                                  fontSize: 11,
                                  fontWeight: 700,
                                  backgroundColor: isInactive
                                    ? statusChipColors.inactive.bg
                                    : cp.bg,
                                  color: isInactive
                                    ? statusChipColors.inactive.color
                                    : cp.color,
                                  borderRadius: '6px',
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ ...tableCellSx, color: colors.textSecondary }}>
                              0
                            </TableCell>
                            <TableCell sx={{ ...tableCellSx, color: colors.textSecondary }}>
                              {formatRelative(r.updated_at)}
                            </TableCell>
                            <TableCell sx={{ ...tableCellSx, textAlign: 'center' }}>
                              <Tooltip title={r.updated_by || 'Unknown'}>
                                <Avatar
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    fontSize: 11,
                                    fontWeight: 700,
                                    backgroundColor: colors.adminAccent,
                                    color: colors.bgWhite,
                                    mx: 'auto',
                                  }}
                                >
                                  {initialsFor(r.updated_by)}
                                </Avatar>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={filtered.length}
                page={page}
                onPageChange={(_, p) => updateSearchParam({ page: p })}
                rowsPerPage={pageSize}
                rowsPerPageOptions={[25, 50, 100, 200]}
                onRowsPerPageChange={(e) => {
                  updateSearchParam({ page: 0, pageSize: Number(e.target.value) });
                }}
              />
            </Paper>
          )}

          {/* Bulk action footer */}
          {selected.size > 0 && (
            <Paper
              elevation={4}
              sx={{
                position: 'fixed',
                left: 0,
                right: 0,
                bottom: 0,
                height: 64,
                zIndex: 10,
                backgroundColor: colors.adminAccent,
                color: colors.bgWhite,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 3,
                borderRadius: 0,
              }}
              role="toolbar"
              aria-label="Bulk actions"
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                  {selected.size} selected
                </Typography>
                <Button
                  size="small"
                  startIcon={<CloseIcon />}
                  onClick={() => setSelected(new Set())}
                  sx={{
                    textTransform: 'none',
                    color: colors.bgWhite,
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
                  }}
                >
                  Clear (Esc)
                </Button>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip
                  title={
                    isSuperAdmin
                      ? 'Publish selected entities (P)'
                      : 'Publish requires super-admin role'
                  }
                >
                  <span>
                    <WriteButton
                      variant="contained"
                      disabled={!isSuperAdmin}
                      onClick={() => setPublishOpen(true)}
                      sx={{
                        // Slate primary per UX brief (not amber).
                        backgroundColor: colors.bgWhite,
                        color: colors.adminAccent,
                        textTransform: 'none',
                        fontWeight: 700,
                        '&:hover': { backgroundColor: colors.adminAccentSubtle },
                        '&.Mui-disabled': {
                          backgroundColor: 'rgba(255,255,255,0.35)',
                          color: 'rgba(255,255,255,0.55)',
                        },
                      }}
                    >
                      Publish selected
                    </WriteButton>
                  </span>
                </Tooltip>
                <Tooltip title="Coming soon">
                  <span>
                    <Button
                      variant="outlined"
                      disabled
                      sx={{
                        textTransform: 'none',
                        borderColor: 'rgba(255,255,255,0.4)',
                        color: colors.bgWhite,
                      }}
                    >
                      Export ZIP
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip
                  title={
                    selected.size === 1
                      ? 'Deactivate selected task'
                      : 'Select exactly 1 task to deactivate (bulk deactivate in Round 4)'
                  }
                >
                  <span>
                    <WriteButton
                      variant="outlined"
                      disabled={selected.size !== 1}
                      onClick={() => {
                        if (selected.size !== 1) return;
                        const onlyCode = Array.from(selected)[0];
                        const row = rows.find((r) => r.task_code === onlyCode);
                        if (row) setDeactivateFor(row);
                      }}
                      sx={{
                        textTransform: 'none',
                        borderColor: 'rgba(255,255,255,0.4)',
                        color: colors.bgWhite,
                        '&.Mui-disabled': {
                          borderColor: 'rgba(255,255,255,0.2)',
                          color: 'rgba(255,255,255,0.4)',
                        },
                      }}
                    >
                      Deactivate
                    </WriteButton>
                  </span>
                </Tooltip>
              </Box>
            </Paper>
          )}
        </>
      )}

      <CreateTaskDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
      />

      {/* Publish confirmation — scoped to currently-selected rows. Rows
          are looked up fresh from `rows` so we use the latest version
          numbers if the list refetched between selection and publish. */}
      <PublishConfirmationDialog
        open={publishOpen}
        onClose={() => setPublishOpen(false)}
        entities={Array.from(selected)
          .map((code) => rows.find((r) => r.task_code === code))
          .filter((r): r is AdminTaskRow => !!r)
          .map<PublishEntity>((r) => toPublishEntity(r))}
        onCompleted={(res) => {
          setPublishOpen(false);
          if (res && res.batch_id) {
            setSnackbar({
              open: true,
              msg: `Publish batch completed`,
              sev: 'success',
            });
            queryClient.invalidateQueries({ queryKey: ['admin', 'tasks', 'list'] });
            // Detail queries for just-published rows need refresh too
            // so the edit page's amber banner disappears on next visit.
            selected.forEach((code) => {
              queryClient.invalidateQueries({ queryKey: ['admin', 'tasks', code] });
            });
            setSelected(new Set());
          }
        }}
      />

      {deactivateFor && (
        <DependencyImpactDialog
          open={!!deactivateFor}
          onClose={() => setDeactivateFor(null)}
          kind={{
            entity: 'task',
            taskCode: deactivateFor.task_code,
            baseVersion: deactivateFor.current_version,
          }}
          onSuccess={(row) => {
            setDeactivateFor(null);
            setSelected(new Set());
            setSnackbar({
              open: true,
              msg: `Deactivated ${row.code}`,
              sev: 'success',
            });
            queryClient.invalidateQueries({ queryKey: ['admin', 'tasks', 'list'] });
            queryClient.invalidateQueries({
              queryKey: ['admin', 'tasks', row.code],
            });
          }}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.sev}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ borderRadius: '8px' }}
        >
          {snackbar.msg}
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default TasksList;
